import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { format, parseISO } from 'date-fns'
import { ru } from 'date-fns/locale'
import Button from '@/components/ui/Button'
import NumberStepper from '@/components/ui/NumberStepper'
import Avatar from '@/components/ui/Avatar'
import type { TimeSlot } from '@/services/schedule'

interface SlotParticipantsSheetProps {
  slot: TimeSlot
  onClose: () => void
  onCancelBooking: (booking: NonNullable<TimeSlot['bookings']>[number]) => void
  cancellingBookingId: string | null
  onSaveCapacity: (capacity: number) => Promise<void>
  onSaveGuest: (details: string, bookingId?: string) => Promise<void>
}

export default function SlotParticipantsSheet({ slot, onClose, onCancelBooking, cancellingBookingId, onSaveCapacity, onSaveGuest }: SlotParticipantsSheetProps) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const participants = slot.bookings ?? []
  const [capacity, setCapacity] = useState(slot.max_clients ?? 1)
  const [editingCapacity, setEditingCapacity] = useState(false)
  const [guestForm, setGuestForm] = useState<{ id?: string; text: string } | null>(null)
  const [pending, setPending] = useState(false)
  const mutationPending = useRef(false)
  const [error, setError] = useState<string | null>(null)
  const busy = pending || cancellingBookingId !== null
  const editable = slot.can_edit === true
  const canAddGuest = editable && (participants.length + (slot.reserved_spots ?? 0)) < (slot.max_clients ?? 1)

  useEffect(() => { setCapacity(slot.max_clients ?? 1) }, [slot.max_clients])

  const save = async (action: () => Promise<void>, done: () => void) => {
    if (busy || mutationPending.current) return
    mutationPending.current = true
    setPending(true)
    setError(null)
    try {
      await action()
      done()
    } catch (err: any) {
      const detail = err.response?.data?.detail
      setError(typeof detail === 'string' ? detail : 'Не удалось сохранить. Попробуйте ещё раз')
    } finally {
      mutationPending.current = false
      setPending(false)
    }
  }

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    const previousFocus = document.activeElement
    const previousOverflow = document.body.style.overflow
    const backButton = window.Telegram?.WebApp.BackButton
    const wasBackVisible = backButton?.isVisible

    dialog.showModal()
    document.body.style.overflow = 'hidden'
    backButton?.onClick(onClose)
    backButton?.show()

    return () => {
      backButton?.offClick(onClose)
      if (!wasBackVisible) backButton?.hide()
      document.body.style.overflow = previousOverflow
      if (dialog.open) dialog.close()
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus()
    }
  }, [onClose])

  return createPortal(
    <dialog
      ref={dialogRef}
      aria-labelledby="slot-participants-title"
      aria-describedby="slot-participants-time"
      className="fixed inset-x-0 bottom-0 top-auto m-0 mx-auto w-full max-w-md max-h-[85dvh] overflow-y-auto rounded-t-3xl border-0 bg-tg-bg p-0 text-tg-text shadow-2xl backdrop:bg-black/40"
      onCancel={(event) => {
        event.preventDefault()
        onClose()
      }}
      onClick={(event) => {
        if (event.target !== event.currentTarget) return
        const bounds = event.currentTarget.getBoundingClientRect()
        if (event.clientX < bounds.left || event.clientX > bounds.right ||
            event.clientY < bounds.top || event.clientY > bounds.bottom) onClose()
      }}
    >
      <div className="sticky top-0 z-10 flex items-start justify-between gap-3 bg-tg-bg px-5 pt-5 pb-4">
        <div>
          <h2 id="slot-participants-title" className="text-xl font-semibold">Кто придёт</h2>
          <p id="slot-participants-time" className="mt-1 text-sm text-tg-hint">
            {format(parseISO(slot.date), 'd MMMM, EEEE', { locale: ru })}
            <span className="mt-0.5 block">{slot.start_time} – {slot.end_time}</span>
          </p>
        </div>
        <button
          type="button"
          aria-label="Закрыть список участников"
          onClick={onClose}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-tg-secondary text-tg-hint focus-visible:outline focus-visible:outline-2 focus-visible:outline-tg-button"
        >
          <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="m6 6 12 12M18 6 6 18" />
          </svg>
        </button>
      </div>

      <div className="px-5" style={{ paddingBottom: 'calc(24px + env(safe-area-inset-bottom, 0px))' }}>
        <p className="mb-4 text-sm font-medium text-tg-hint">
          Записано {participants.length} из {slot.max_clients ?? 1}
        </p>
        {(slot.pair_proposals ?? []).map(p => <p key={p.id} className="mb-3 rounded-xl bg-tg-secondary p-3 text-sm">Резерв пары: {p.names}. Ожидается подтверждение до {format(parseISO(p.deadline), 'HH:mm')} по вашему времени. Мест в резерве: {p.reserved_spots}</p>)}
        {error && <p role="alert" className="mb-4 text-sm text-red-600">{error}</p>}
        {editable && !guestForm && (
          <div className="mb-4">
            {editingCapacity ? (
              <form onSubmit={event => {
                event.preventDefault()
                void save(() => onSaveCapacity(capacity), () => setEditingCapacity(false))
              }} className="space-y-3 rounded-xl bg-tg-secondary p-3">
                <p className="text-sm font-medium">Количество человек на одно время</p>
                <NumberStepper label="Количество мест в слоте" value={capacity} min={Math.max(1, participants.length + (slot.reserved_spots ?? 0))} max={2147483647} onChange={setCapacity} />
                <p className="text-xs text-tg-hint">Только для этого слота. Расписание недели не изменится</p>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" disabled={busy} onClick={() => setEditingCapacity(false)}>Отмена</Button>
                  <Button type="submit" loading={pending} disabled={busy}>Сохранить</Button>
                </div>
              </form>
            ) : (
              <button type="button" disabled={busy} onClick={() => { setError(null); setEditingCapacity(true) }} className="min-h-11 text-sm font-medium text-tg-button">Изменить количество мест</button>
            )}
          </div>
        )}
        {guestForm && (
          <form onSubmit={event => {
            event.preventDefault()
            if (!guestForm.text.trim()) return
            void save(() => onSaveGuest(guestForm.text.trim(), guestForm.id), () => setGuestForm(null))
          }} className="mb-4 space-y-3 rounded-xl bg-tg-secondary p-3">
            <label className="block text-sm font-medium" htmlFor="guest-details">{guestForm.id ? 'Изменить гостевую запись' : 'Записать гостя'}</label>
            <input id="guest-details" autoFocus required maxLength={1000} value={guestForm.text} disabled={busy}
              onChange={event => setGuestForm({ ...guestForm, text: event.target.value })}
              placeholder="Алексей, пробное от зала"
              className="w-full rounded-lg border border-gray-300 bg-tg-bg p-3 text-tg-text" />
            <p className="text-xs text-tg-hint">Любая информация о госте. Видна только вам</p>
            <div className="flex gap-2">
              <Button type="button" variant="outline" disabled={busy} onClick={() => setGuestForm(null)}>Отмена</Button>
              <Button type="submit" loading={pending} disabled={busy || !guestForm.text.trim()}>{guestForm.id ? 'Сохранить' : 'Записать'}</Button>
            </div>
          </form>
        )}
        {participants.length > 0 ? (
          <ul className="space-y-3">
            {participants.map((participant) => (
              <li key={participant.id} className="flex items-center gap-3 rounded-xl bg-tg-secondary p-3">
                {!participant.guest_details && (
                  <Avatar src={participant.client_photo_url} name={participant.client_name} size="lg" className="shrink-0" />
                )}
                <div className="min-w-0 flex-1 break-words [overflow-wrap:anywhere]">
                  <span className="font-medium">{participant.client_name}</span>
                  {participant.pair_status === 'accepted' && <span className="mt-0.5 block text-xs text-tg-hint">В паре с {participant.pair_partner_name}</span>}
                  {participant.guest_details && <span className="mt-0.5 block text-xs text-tg-hint">Гостевая запись</span>}
                </div>
                {participant.guest_details && editable && !guestForm && !editingCapacity && (
                  <button type="button" aria-label={`Изменить запись ${participant.client_name}`} disabled={busy}
                    onClick={() => { setError(null); setGuestForm({ id: participant.id, text: participant.guest_details! }) }}
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-tg-button disabled:opacity-50">
                    <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m16 3 5 5-12 12-6 1 1-6L16 3ZM14 5l5 5" /></svg>
                  </button>
                )}
                <button
                  type="button"
                  aria-label={`${cancellingBookingId === participant.id ? 'Отменяем запись' : 'Отменить запись'} ${participant.client_name}`}
                  title={`Отменить запись ${participant.client_name}`}
                  aria-busy={cancellingBookingId === participant.id}
                  disabled={busy || guestForm !== null || editingCapacity}
                  onClick={() => onCancelBooking(participant)}
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-red-600 hover:bg-red-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-tg-button disabled:opacity-50"
                >
                  {cancellingBookingId === participant.id ? (
                    <svg aria-hidden="true" className="animate-spin" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="M12 3a9 9 0 1 1-9 9" />
                    </svg>
                  ) : (
                    <svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <circle cx="12" cy="12" r="9" />
                      <path d="M8 12h8" />
                    </svg>
                  )}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="rounded-xl bg-tg-secondary px-4 py-6 text-center text-tg-hint">
            Пока никто не записан
          </p>
        )}
        {canAddGuest && !guestForm && !editingCapacity && (
          <Button fullWidth className="mt-4" disabled={busy} onClick={() => { setError(null); setGuestForm({ text: '' }) }}>Записать гостя</Button>
        )}
      </div>
    </dialog>,
    document.body
  )
}
